import React, { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import PropertyBasicInfoSection from "./PropertyFormSections/PropertyBasicInfoSection";
import PropertyAddressSection from "./PropertyFormSections/PropertyAddressSection";
import PropertyDetailsSection from "./PropertyFormSections/PropertyDetailsSection";
import BuildingDetailsSection from "./PropertyFormSections/BuildingDetailsSection";
import PropertyPricingSection from "./PropertyFormSections/PropertyPricingSection";
import PropertyImagesSection from "./PropertyFormSections/PropertyImagesSection";
import PropertyLandlordSection from "./PropertyFormSections/PropertyLandlordSection";

// ── Styles ─────────────────────────────────────────────────────────────────────
const ms = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 860,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  head: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1d23",
    flexShrink: 0,
  },
  body: {
    padding: "1.25rem",
    overflowY: "auto",
    flex: 1,
  },
  foot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 16px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 18px",
    borderRadius: 6,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 20,
    lineHeight: 1,
    color: "#6b7280",
    padding: "0 2px",
  },
  errorBanner: {
    fontSize: 12,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "8px 12px",
    marginBottom: "1rem",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 6,
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const getStatusOptionsByType = (propertyType) => {
  const statusMap = {
    sale: [
      { value: "available", label: "Available" },
      { value: "under_contract", label: "Under Contract" },
      { value: "sold", label: "Sold" },
      { value: "withdrawn", label: "Withdrawn" },
    ],
    one_time_rental: [
      { value: "available", label: "Available" },
      { value: "pending", label: "Pending" },
      { value: "rented", label: "Rented" },
      { value: "withdrawn", label: "Withdrawn" },
    ],
    property_management: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "terminated", label: "Terminated" },
    ],
    multi_unit_building: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "under_renovation", label: "Under Renovation" },
    ],
  };
  return statusMap[propertyType] || statusMap.one_time_rental;
};

const initializeFormData = (property) => ({
  title: property?.title || "",
  buildingName: property?.buildingName || "",
  description: property?.description || "",
  type: property?.type || "one_time_rental",
  status: property?.status || "available",
  street: property?.address?.street || "",
  city: property?.address?.city || "",
  parish: property?.address?.parish || "",
  zipCode: property?.address?.zipCode || "",
  bedrooms: property?.propertyDetails?.bedrooms || "",
  bathrooms: property?.propertyDetails?.bathrooms || "",
  squareFootage: property?.propertyDetails?.squareFootage || "",
  lotSize: property?.propertyDetails?.lotSize || "",
  propertyType: property?.propertyDetails?.propertyType || "",
  yearBuilt: property?.propertyDetails?.yearBuilt || "",
  parking: property?.propertyDetails?.parking || "",
  amenities: property?.amenities || [],
  rent: property?.pricing?.rent || "",
  salePrice: property?.pricing?.salePrice || "",
  deposit: property?.pricing?.deposit || "",
  utilities: property?.pricing?.utilities || "tenant_pays",
  totalUnits: property?.buildingDetails?.totalUnits || "",
  floors: property?.buildingDetails?.floors || "",
  buildingYearBuilt: property?.buildingDetails?.yearBuilt || "",
  buildingType: property?.buildingDetails?.buildingType || "",
  buildingAmenities: property?.amenities || [],
  images: property?.images || [],
  landlordName: property?.placementInfo?.landlordInfo?.landlordName || "",
  landlordContact: property?.placementInfo?.landlordInfo?.landlordContact || "",
  ownerName: property?.managementInfo?.ownerInfo?.ownerName || "",
  ownerContact: property?.managementInfo?.ownerInfo?.ownerContact || "",
  ownerEmail: property?.managementInfo?.ownerInfo?.ownerEmail || "",
  sellerName: property?.saleInfo?.sellerInfo?.sellerName || "",
  sellerContact: property?.saleInfo?.sellerInfo?.sellerContact || "",
  saleCommissionRate:
    property?.pricing?.saleCommission?.rate != null
      ? String(property.pricing.saleCommission.rate)
      : "",
  saleCommissionPaidBy: property?.pricing?.saleCommission?.paidBy || "seller",
  buildingContactName: property?.propertyManager?.name || "",
  buildingContactPhone: property?.propertyManager?.contact || "",
  buildingContactEmail: property?.propertyManager?.email || "",
});

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });

const buildPropertyPayload = async (formData, isMultiUnitBuilding, property, isCreateMode) => {
  const payload = {
    type: formData.type,
    status: formData.status,
    address: {
      street: formData.street,
      city: formData.city,
      parish: formData.parish,
      zipCode: formData.zipCode,
    },
  };

  if (isMultiUnitBuilding) {
    payload.buildingName = formData.buildingName;
    payload.title = formData.title;
    payload.description = formData.description;
    payload.buildingDetails = {
      totalUnits: parseInt(formData.totalUnits) || 0,
      floors: parseInt(formData.floors) || 0,
      yearBuilt: parseInt(formData.buildingYearBuilt) || null,
      buildingType: formData.buildingType,
    };
    payload.amenities = formData.buildingAmenities || [];
  } else {
    payload.title = formData.title;
    payload.description = formData.description;
    const yb = parseInt(formData.yearBuilt, 10);
    const isLand = formData.propertyType === "land";
    const prevDetails =
      !isCreateMode && property?.propertyDetails
        ? { ...(property.propertyDetails.toObject?.() || property.propertyDetails) }
        : {};
    payload.propertyDetails = {
      ...prevDetails,
      propertyType: formData.propertyType,
      yearBuilt: !Number.isNaN(yb) ? yb : null,
    };
    if (!isLand) {
      payload.propertyDetails.bedrooms = parseInt(formData.bedrooms, 10) || 0;
      payload.propertyDetails.bathrooms = parseFloat(formData.bathrooms) || 0;
      payload.propertyDetails.squareFootage = parseInt(formData.squareFootage, 10) || 0;
      const lot = parseInt(formData.lotSize, 10);
      if (!Number.isNaN(lot) && lot > 0) payload.propertyDetails.lotSize = lot;
      else delete payload.propertyDetails.lotSize;
    } else {
      delete payload.propertyDetails.bedrooms;
      delete payload.propertyDetails.bathrooms;
      const lot = parseInt(formData.lotSize, 10);
      if (!Number.isNaN(lot) && lot > 0) payload.propertyDetails.lotSize = lot;
      else delete payload.propertyDetails.lotSize;
      const sq = parseInt(formData.squareFootage, 10);
      if (!Number.isNaN(sq) && sq > 0) payload.propertyDetails.squareFootage = sq;
      else delete payload.propertyDetails.squareFootage;
    }
    payload.amenities = formData.amenities || [];
    payload.pricing = {
      rent: parseFloat(formData.rent) || null,
      salePrice: parseFloat(formData.salePrice) || null,
      deposit: parseFloat(formData.deposit) || null,
      utilities: formData.utilities || "tenant_pays",
    };
    if (formData.type === "sale") {
      const rate = parseFloat(formData.saleCommissionRate, 10);
      payload.pricing.saleCommission = {
        ...(isCreateMode ? {} : property?.pricing?.saleCommission || {}),
        type: "percentage",
        rate: Number.isFinite(rate) ? rate : 0,
        paidBy: formData.saleCommissionPaidBy || "seller",
      };
    }
  }

  if (formData.images && formData.images.length > 0) {
    payload.images = await Promise.all(
      formData.images.map(async (image) => {
        if (image.file && image.isNew) {
          const base64Url = await fileToBase64(image.file);
          return { url: base64Url, caption: image.caption || "", isPrimary: image.isPrimary || false };
        }
        return {
          url: typeof image === "string" ? image : image.url,
          caption: image.caption || "",
          isPrimary: image.isPrimary || false,
        };
      })
    );
  }

  appendLandlordAndOwnerToPayload(payload, formData, isMultiUnitBuilding, isCreateMode);
  return payload;
};

const appendLandlordAndOwnerToPayload = (payload, formData, isMultiUnitBuilding, isCreateMode) => {
  if (isMultiUnitBuilding) {
    if (isCreateMode) {
      payload.propertyManager = {
        name: (formData.buildingContactName || "").trim(),
        contact: (formData.buildingContactPhone || "").trim(),
        email: (formData.buildingContactEmail || "").trim(),
      };
    } else {
      payload["propertyManager.name"] = (formData.buildingContactName || "").trim();
      payload["propertyManager.contact"] = (formData.buildingContactPhone || "").trim();
      payload["propertyManager.email"] = (formData.buildingContactEmail || "").trim();
    }
    return;
  }
  const t = formData.type;
  if (t === "one_time_rental") {
    if (isCreateMode) {
      payload.placementInfo = {
        tenantPlaced: false,
        applicationPending: false,
        landlordInfo: {
          landlordName: (formData.landlordName || "").trim(),
          landlordContact: (formData.landlordContact || "").trim(),
        },
      };
    } else {
      payload["placementInfo.landlordInfo.landlordName"] = (formData.landlordName || "").trim();
      payload["placementInfo.landlordInfo.landlordContact"] = (formData.landlordContact || "").trim();
    }
    return;
  }
  if (t === "property_management") {
    if (isCreateMode) {
      payload.managementInfo = {
        isActive: true,
        ownerInfo: {
          ownerName: (formData.ownerName || "").trim(),
          ownerContact: (formData.ownerContact || "").trim(),
          ownerEmail: (formData.ownerEmail || "").trim(),
        },
      };
    } else {
      payload["managementInfo.ownerInfo.ownerName"] = (formData.ownerName || "").trim();
      payload["managementInfo.ownerInfo.ownerContact"] = (formData.ownerContact || "").trim();
      payload["managementInfo.ownerInfo.ownerEmail"] = (formData.ownerEmail || "").trim();
    }
    return;
  }
  if (t === "sale") {
    if (isCreateMode) {
      payload.saleInfo = {
        isSold: false,
        underContract: false,
        sellerInfo: {
          sellerName: (formData.sellerName || "").trim(),
          sellerContact: (formData.sellerContact || "").trim(),
        },
      };
    } else {
      payload["saleInfo.sellerInfo.sellerName"] = (formData.sellerName || "").trim();
      payload["saleInfo.sellerInfo.sellerContact"] = (formData.sellerContact || "").trim();
    }
  }
};

const validateLandlordOwnerFields = (formData, isMultiUnitBuilding) => {
  if (isMultiUnitBuilding) {
    if (!(formData.buildingContactName || "").trim() || !(formData.buildingContactPhone || "").trim())
      return "Building owner or manager name and phone are required.";
    return null;
  }
  if (formData.type === "one_time_rental") {
    if (!(formData.landlordName || "").trim() || !(formData.landlordContact || "").trim())
      return "Landlord name and phone are required.";
    return null;
  }
  if (formData.type === "property_management") {
    if (!(formData.ownerName || "").trim() || !(formData.ownerContact || "").trim() || !(formData.ownerEmail || "").trim())
      return "Owner name, phone, and email are required.";
    return null;
  }
  if (formData.type === "sale") {
    if (!(formData.sellerName || "").trim() || !(formData.sellerContact || "").trim())
      return "Seller name and phone are required.";
    const rate = parseFloat(formData.saleCommissionRate, 10);
    if (Number.isNaN(rate) || rate < 0 || rate > 100)
      return "Enter a valid sale commission percentage between 0 and 100.";
    return null;
  }
  return null;
};

// ── Component ──────────────────────────────────────────────────────────────────
function PropertyFormModal({ show, onHide, property, onPropertyUpdated, mode = "edit" }) {
  const isCreateMode = mode === "create";
  const [formData, setFormData] = useState(() => initializeFormData(property));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    if (show) {
      setFormData(initializeFormData(isCreateMode ? null : property));
      setError("");
    }
  }, [show, property, isCreateMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "type") {
      const defaultStatusMap = {
        sale: "available",
        one_time_rental: "available",
        property_management: "active",
        multi_unit_building: "active",
      };
      setFormData((prev) => ({ ...prev, [name]: value, status: defaultStatusMap[value] || "available" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAmenitiesChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      buildingAmenities: checked
        ? [...(prev.buildingAmenities || []), value]
        : (prev.buildingAmenities || []).filter((a) => a !== value),
    }));
  };

  const handlePropertyAmenitiesChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      amenities: checked
        ? [...(prev.amenities || []), value]
        : (prev.amenities || []).filter((a) => a !== value),
    }));
  };

  const handleImagesChange = (images) => setFormData((prev) => ({ ...prev, images }));

  const isMultiUnitBuilding = formData.type === "multi_unit_building";
  const getStatusOptions = () => getStatusOptionsByType(formData.type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const validationError = validateLandlordOwnerFields(formData, isMultiUnitBuilding);
      if (validationError) { setError(validationError); setLoading(false); return; }

      const payload = await buildPropertyPayload(formData, isMultiUnitBuilding, property, isCreateMode);
      const endpoint = isCreateMode ? API_ENDPOINTS.properties.create : API_ENDPOINTS.properties.update(property._id);
      const method = isCreateMode ? "POST" : "PUT";

      const response = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isCreateMode ? "create" : "update"} property`);
      }

      const result = await response.json();
      const isMultiUnitCreation = isCreateMode && isMultiUnitBuilding;
      onPropertyUpdated(result.data, isCreateMode, isMultiUnitCreation);
      onHide();
    } catch (err) {
      setError(err.message || `An error occurred while ${isCreateMode ? "creating" : "updating"} the property`);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={ms.overlay} onClick={() => { if (!loading) onHide(); }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ms.modal} onClick={(e) => e.stopPropagation()}>
        <div style={ms.head}>
          {isCreateMode ? "Create New Property" : "Edit Property"}
          {!loading && (
            <button style={ms.closeBtn} onClick={onHide}>×</button>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
          <div style={ms.body}>
            {error && <div style={ms.errorBanner}>{error}</div>}

            <PropertyBasicInfoSection
              formData={formData}
              handleInputChange={handleInputChange}
              isCreateMode={isCreateMode}
              isMultiUnitBuilding={isMultiUnitBuilding}
              getStatusOptions={getStatusOptions}
            />
            <PropertyAddressSection formData={formData} handleInputChange={handleInputChange} />
            <PropertyLandlordSection formData={formData} handleInputChange={handleInputChange} isMultiUnitBuilding={isMultiUnitBuilding} />
            <PropertyImagesSection formData={formData} handleImagesChange={handleImagesChange} />

            {isMultiUnitBuilding ? (
              <BuildingDetailsSection
                formData={formData}
                handleInputChange={handleInputChange}
                handleAmenitiesChange={handleAmenitiesChange}
              />
            ) : (
              <>
                <PropertyDetailsSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleAmenitiesChange={handlePropertyAmenitiesChange}
                />
                <PropertyPricingSection formData={formData} handleInputChange={handleInputChange} />
              </>
            )}
          </div>

          <div style={ms.foot}>
            <button type="button" style={ms.btn} onClick={onHide} disabled={loading}>
              Cancel
            </button>
            <button type="submit" style={ms.btnPrimary} disabled={loading}>
              {loading ? (
                <><span style={ms.spinner} />{isCreateMode ? "Creating…" : "Updating…"}</>
              ) : (
                isCreateMode ? "Create Property" : "Update Property"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PropertyFormModal;
